import { v4 as uuidv4 } from "uuid";
import { createEmptyGroup } from "../../../../components/queryBuilder/queryBuilderUtils";
import type { ConditionFieldListOptions } from "../../../../data-model/pharmacyOrderUpdatesPaRequestsMetadata";
import type {
  PocReassignmentDoc,
  PocReassignmentEditContext,
  PocReassignmentRuleFormData,
  PocReassignmentRuleState,
} from "../../../../data-model/pocReassignmentRule";
import {
  getBooleanOperators,
  getNumericOperators,
  getStringOperators,
  getTimestampOperators,
  getUnknownDatatypeOperators,
  type FieldDefinition,
  type QueryBuilderConfig,
  type QueryBuilderValue,
  type QueryCondition,
  type QueryNode,
} from "../../../../data-model/queryBuilder";
import type {
  CmmInputUniqueValueItem,
  CmmInputUniqueValuesColumn,
  CmmInputUniqueValuesDoc,
} from "../validationFunnel/pages/conditionList/cmmInputUniqueValuesTypes";

const DEFAULT_LOOKBACK = "0 days";

export const DEFAULT_POC_REASSIGNMENT_DOC_ID = "poc_reassignment";

/** Some legacy Firestore docs spell the array `childeren` instead of `children`. Read either; always emit `children`. */
type LegacyChildrenContainer = {
  children?: QueryNode[];
  childeren?: QueryNode[];
};

/** Coerce a bare `QueryCondition` into a `QueryGroup`. The sample poc_reassignment doc stores `rule` as a top-level condition (not wrapped in an AND-group). */
function ensureGroupShape(raw: unknown): QueryBuilderValue {
  if (raw == null) return createEmptyGroup();
  const node = raw as { type?: string };
  if (node.type === "condition") {
    return {
      type: "group",
      id: uuidv4(),
      conjunction: "AND",
      children: [raw as QueryCondition],
    };
  }
  return raw as QueryBuilderValue;
}

/** Ensure values are string | string[] AND inject ids on groups/conditions — Firestore docs may store them without ids, but QueryBuilder uses `id` for change handlers and React keys. Tolerates the `childeren` typo on input. */
function normalizeConditionForQueryBuilder(
  condition: QueryBuilderValue,
): QueryBuilderValue {
  const legacy = condition as unknown as LegacyChildrenContainer;
  const rawChildren: QueryNode[] = legacy.children ?? legacy.childeren ?? [];
  return {
    type: "group",
    id: condition.id ?? uuidv4(),
    conjunction: condition.conjunction ?? "AND",
    children: rawChildren.map((child) =>
      child.type === "condition"
        ? {
            ...child,
            id: child.id ?? uuidv4(),
            value:
              typeof child.value === "number"
                ? String(child.value)
                : Array.isArray(child.value)
                  ? child.value.map((v) =>
                      typeof v === "number" ? String(v) : v,
                    )
                  : (child.value ?? ""),
          }
        : normalizeConditionForQueryBuilder(child as QueryBuilderValue),
    ),
  };
}

/** Coerce a legacy single-string `value` into the canonical string[] used in the UI. */
function normalizeActionValue(value: string | string[] | undefined): string[] {
  if (Array.isArray(value)) return value.map((v) => String(v));
  if (value == null || value === "") return [];
  return [String(value)];
}

export function mapPocReassignmentDocToRuleStates(
  doc: PocReassignmentDoc,
): PocReassignmentRuleState[] {
  return (doc.rules ?? []).map((rule, index) => {
    const conditionKey: "rule" | "when" =
      rule.rule != null ? "rule" : rule.when != null ? "when" : "rule";
    const rawCondition = rule.rule ?? rule.when;
    const wrapped =
      rawCondition != null
        ? ensureGroupShape(rawCondition)
        : createEmptyGroup();
    return {
      id: `poc-reassignment-rule-${index}-${uuidv4()}`,
      label: rule.label ?? `Rule ${index + 1}`,
      subtext: rule.subtext ?? "",
      active: rule.active ?? true,
      lookback: rule.lookback ?? DEFAULT_LOOKBACK,
      actions: (rule.actions ?? []).map((a) => ({
        type: a.type ?? "set",
        field: a.field ?? "",
        value: normalizeActionValue(a.value),
      })),
      triggers: (rule.triggers ?? []).map((t) => ({ ...t })),
      condition: normalizeConditionForQueryBuilder(wrapped),
      conditionKey,
    };
  });
}

export function mapRuleStatesToFirestorePayload(
  rules: PocReassignmentRuleState[],
  existingDoc: PocReassignmentDoc | null,
  updatedBy?: string | null,
): Pick<
  PocReassignmentDoc,
  "id" | "rules" | "created_at" | "created_by" | "updated_at" | "updated_by"
> {
  const nowIso = new Date().toISOString();
  return {
    id: existingDoc?.id ?? DEFAULT_POC_REASSIGNMENT_DOC_ID,
    rules: rules.map((r) => {
      const conditionField =
        r.conditionKey === "when"
          ? { when: r.condition }
          : { rule: r.condition };
      return {
        label: r.label,
        subtext: r.subtext ?? "",
        active: r.active,
        lookback: r.lookback,
        actions: r.actions.map((a) => ({
          type: a.type,
          field: a.field,
          value: Array.isArray(a.value) ? [...a.value] : a.value,
        })),
        triggers: r.triggers.map((t) => ({ ...t })),
        ...conditionField,
      };
    }),
    ...(existingDoc?.created_at != null && {
      created_at: existingDoc.created_at,
    }),
    ...(existingDoc?.created_by != null && {
      created_by: existingDoc.created_by,
    }),
    updated_at: nowIso,
    ...(updatedBy != null && updatedBy !== "" && { updated_by: updatedBy }),
  };
}

export function createEmptyPocReassignmentRule(
  index: number,
): PocReassignmentRuleState {
  return {
    id: `poc-reassignment-rule-${uuidv4()}`,
    label: `Rule ${index + 1}`,
    subtext: "",
    active: true,
    lookback: DEFAULT_LOOKBACK,
    actions: [{ type: "set", field: "poc", value: "" }],
    triggers: [{ type: "api" }],
    condition: createEmptyGroup(),
    conditionKey: "rule",
  };
}

export const getDefaultPocReassignmentRules =
  (): PocReassignmentRuleState[] => [createEmptyPocReassignmentRule(0)];

export function getInitialFormDataForPocReassignmentModal(
  editContext: PocReassignmentEditContext,
  rules: PocReassignmentRuleState[],
): PocReassignmentRuleFormData | undefined {
  if (editContext == null) return undefined;
  const rule = rules.find((r) => r.id === editContext.ruleId);
  if (!rule) return undefined;
  return {
    label: rule.label,
    subtext: rule.subtext ?? "",
    active: rule.active,
    lookback: rule.lookback,
    actions: rule.actions.map((a) => ({
      ...a,
      value: Array.isArray(a.value) ? [...a.value] : a.value,
    })),
    triggers: rule.triggers.map((t) => ({ ...t })),
  };
}

function topValueToOption(
  t: CmmInputUniqueValueItem | string | number | boolean,
): { value: string; label: string } {
  const isItemShape = t != null && typeof t === "object" && "value" in t;
  const value = isItemShape ? (t as CmmInputUniqueValueItem).value : t;
  const count = isItemShape ? (t as CmmInputUniqueValueItem).count : undefined;
  const valueStr = String(value ?? "").slice(0, 80);
  const suffix = String(value ?? "").length > 80 ? "…" : "";
  const countPart = count !== undefined ? ` (${count})` : "";
  return {
    value: String(value ?? ""),
    label: `${valueStr}${suffix}${countPart}`,
  };
}

function buildFieldFromCmmColumn(
  col: CmmInputUniqueValuesColumn,
): FieldDefinition {
  const valueOptionsFromTopValues =
    col.top_values?.length > 0
      ? col.top_values.slice(0, 100).map(topValueToOption)
      : undefined;

  const dt = (col.data_type ?? "").toUpperCase();
  let operators: ReturnType<typeof getStringOperators>;

  if (dt === "STRING") {
    operators = getStringOperators(valueOptionsFromTopValues ?? undefined);
  } else if (
    dt === "INTEGER" ||
    dt === "FLOAT" ||
    dt === "FLOAT64" ||
    dt === "FLOAT32" ||
    dt === "INT64" ||
    dt === "INT32"
  ) {
    operators = getNumericOperators();
  } else if (dt === "BOOLEAN") {
    operators = getBooleanOperators();
  } else if (dt === "TIMESTAMP" || dt === "DATE" || dt === "DATETIME") {
    operators = getTimestampOperators(valueOptionsFromTopValues ?? undefined);
  } else {
    operators = getUnknownDatatypeOperators(
      valueOptionsFromTopValues ?? undefined,
    );
  }

  const rawLabel = col.display_name || col.column_name.replace(/_/g, " ");
  return {
    value: col.column_name,
    label: rawLabel.charAt(0).toUpperCase() + rawLabel.slice(1),
    operators,
    dataType: col.data_type,
    valueOptions: valueOptionsFromTopValues,
  };
}

/**
 * Builds QueryBuilderConfig for the POC reassignment page from the CMM input unique
 * values Firestore document.
 *
 * @param doc - CMM input unique values document from Firestore
 * @param options - When `filterByShowOnConditionList` is false, all columns are included
 * @returns Query builder config with field definitions derived from document columns
 */
export function buildPocReassignmentConditionConfigFromCmmColumns(
  doc: CmmInputUniqueValuesDoc | null,
  options?: ConditionFieldListOptions,
): QueryBuilderConfig {
  if (!doc?.columns?.length) {
    return { fields: [] };
  }
  const filterByShowOnConditionList =
    options?.filterByShowOnConditionList !== false;
  const columns = filterByShowOnConditionList
    ? doc.columns.filter((col) => col.show_on_condition_list)
    : doc.columns;
  return {
    fields: columns.map(buildFieldFromCmmColumn),
  };
}

/** POC reassignment condition dropdowns include columns hidden from other config pages. */
export const POC_REASSIGNMENT_CONDITION_FIELD_OPTIONS: ConditionFieldListOptions =
  {
    filterByShowOnConditionList: false,
  };

export const POC_REASSIGNMENT_CONDITION_TITLE = "Conditions (All Must Match)";
